import { z } from 'zod';
import { schemaDateHeure } from './commun';

/** Permissions élémentaires (matrice des habilitations, section 1). */
export const PERMISSIONS = [
  'voir',
  'creer',
  'modifier',
  'transmettre',
  'valider',
  'decider',
  'signer',
  'refuser',
  'archiver',
  'exporter',
  'administrer',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** Domaines de permissions (matrice des habilitations, section 1). */
export const DOMAINES = [
  'courrier',
  'dossier',
  'demarche_config',
  'document',
  'budget',
  'depense',
  'recette',
  'caisse',
  'marche_municipal',
  'domaine_public',
  'patrimoine',
  'personnel',
  'personnel_sensible',
  'intervention',
  'dechets',
  'travaux',
  'achat',
  'assemblee',
  'quartier',
  'urbanisme',
  'equipement',
  'social',
  'evenement',
  'reclamation',
  'reclamation_sensible',
  'inspection',
  'messagerie',
  'parametres',
  'utilisateurs',
  'audit',
  'supervision',
] as const;
export type Domaine = (typeof DOMAINES)[number];

/** Domaines sensibles : la permission du rôle n'est effective qu'avec une habilitation nominative (D-023). */
export const DOMAINES_SENSIBLES: Partial<Record<Domaine, 'personnel_sensible' | 'social' | 'reclamation_sensible'>> = {
  personnel_sensible: 'personnel_sensible',
  social: 'social',
  reclamation_sensible: 'reclamation_sensible',
};

/** Permissions qu'une délégation peut étendre (matrice, section 1). */
export const PERMISSIONS_DELEGABLES = ['valider', 'decider', 'signer'] as const;

/** Domaines dont la présence rend un rôle sensible (quatre yeux, D-022). */
export const DOMAINES_RENDANT_SENSIBLE: Domaine[] = [
  'budget',
  'depense',
  'recette',
  'caisse',
  'personnel',
  'personnel_sensible',
  'social',
];

export const schemaDomaine = z.enum(DOMAINES);
export const schemaPermission = z.enum(PERMISSIONS);

export const schemaPermissionsRole = z
  .partialRecord(schemaDomaine, z.array(schemaPermission).min(1).max(PERMISSIONS.length))
  .refine((p) => Object.keys(p).length > 0, 'Au moins un domaine est requis');
export type PermissionsRole = Partial<Record<Domaine, Permission[]>>;

export const schemaCreationRole = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code en minuscules, chiffres et soulignés'),
  libelle: z.string().trim().min(2).max(120),
  permissions: schemaPermissionsRole,
});

export const schemaAttributionRole = z.object({
  role_id: z.string().uuid(),
});

export const ZONES_HABILITATION = ['personnel_sensible', 'social', 'reclamation_sensible', 'mission'] as const;

export const schemaCreationHabilitation = z
  .object({
    appartenance_id: z.string().uuid(),
    zone: z.enum(ZONES_HABILITATION),
    perimetre: z
      .object({
        domaines: z.array(schemaDomaine).optional(),
      })
      .default({}),
    motif: z.string().trim().min(3).max(500),
    debut: schemaDateHeure,
    fin: schemaDateHeure,
  })
  .refine((v) => Date.parse(v.fin) > Date.parse(v.debut), { message: 'La fin doit être postérieure au début', path: ['fin'] })
  .refine((v) => v.zone !== 'mission' || (v.perimetre.domaines?.length ?? 0) > 0, {
    message: 'Une habilitation de mission précise ses domaines',
    path: ['perimetre'],
  });

export const TYPES_DELEGATION = ['signature', 'fonction', 'interim'] as const;

export const schemaCreationDelegation = z
  .object({
    type: z.enum(TYPES_DELEGATION),
    delegataire_appartenance_id: z.string().uuid(),
    domaines: z.array(schemaDomaine).min(1),
    demarche_ids: z.array(z.string().uuid()).default([]),
    plafond_montant: z.number().int().nonnegative().nullable().default(null),
    debut: schemaDateHeure,
    fin: schemaDateHeure,
    acte_document_id: z.string().uuid().nullable().default(null),
  })
  .refine((v) => Date.parse(v.fin) > Date.parse(v.debut), { message: 'La fin doit être postérieure au début', path: ['fin'] });

export const schemaRevocation = z.object({
  motif: z.string().trim().min(3).max(500),
});

/** Rôles de référence pour lesquels le second facteur est obligatoire (D-025). */
export const ROLES_MFA_OBLIGATOIRE = [
  'maire_president',
  'secretaire_general',
  'responsable_financier',
  'responsable_rh',
  'administrateur_local',
] as const;
